import { z } from 'zod';
import { TUserRoles } from '@/features/users/schemas/users.schema.ts';

const ASSIGNABLE_ROLES = [
  'Company owner',
  'Company administrator',
  'Installer',
  'Owner',
  'Guest',
] as const satisfies readonly TUserRoles[];

const INVITEE_ALL_PLANTS_ROLES = ['Company owner', 'Company administrator'] as const;
const INVITEE_SCOPED_PLANTS_ROLES = ['Installer', 'Owner', 'Guest'] as const;

type AssignableRole = (typeof ASSIGNABLE_ROLES)[number];

export const WELCOME_MESSAGE_MAX_LENGTH = 500;

export const getAssignableRoles = (inviterRole: TUserRoles): readonly AssignableRole[] => {
  if (inviterRole === 'Company owner' || inviterRole === 'Company administrator') {
    return ASSIGNABLE_ROLES;
  }
  if (inviterRole === 'Installer') {
    return ['Owner', 'Guest'] as const;
  }
  return [] as const;
};

const inviteeRoleSchema = z.enum(ASSIGNABLE_ROLES);

export const inviteUserIdentityFormSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required'),
  lastName: z.string().trim().min(1, 'Last name is required'),
  email: z.string().trim().email('Enter a valid email address'),
  role: inviteeRoleSchema,
});

export const inviteUserPlantsFormSchema = inviteUserIdentityFormSchema.extend({
  plantIds: z.array(z.string()).optional(),
});

export const inviteUserWelcomeFormSchema = inviteUserPlantsFormSchema.extend({
  welcomeMessage: z
    .string()
    .max(WELCOME_MESSAGE_MAX_LENGTH, `Maximum ${WELCOME_MESSAGE_MAX_LENGTH} characters`)
    .optional(),
});

export const inviteUserSchema = inviteUserWelcomeFormSchema.superRefine((data, ctx) => {
  if ((INVITEE_ALL_PLANTS_ROLES as readonly string[]).includes(data.role)) {
    if (data.plantIds && data.plantIds.length > 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['plantIds'],
        message: 'Company owner and Company administrator receive access to all plants',
      });
    }
    return;
  }

  if ((INVITEE_SCOPED_PLANTS_ROLES as readonly string[]).includes(data.role)) {
    if (!Array.isArray(data.plantIds)) {
      ctx.addIssue({
        code: 'custom',
        path: ['plantIds'],
        message: 'Plant assignment is required',
      });
    }
  }
});

export type InviteUserFormData = z.infer<typeof inviteUserSchema>;

export type InviteUserPayload = {
  firstName: string;
  lastName: string;
  email: string;
  role: AssignableRole;
  plantIds?: string[];
  welcomeMessage?: string;
};

export const toInviteUserPayload = (data: InviteUserFormData): InviteUserPayload => {
  const payload: InviteUserPayload = {
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    role: data.role,
  };

  if (
    (INVITEE_SCOPED_PLANTS_ROLES as readonly string[]).includes(data.role) &&
    data.plantIds
  ) {
    payload.plantIds = data.plantIds;
  }

  if (data.welcomeMessage && data.welcomeMessage.length > 0) {
    payload.welcomeMessage = data.welcomeMessage;
  }

  return payload;
};
